import React from "react"
import {v4 as uuid} from "uuid"
import {isEmpty} from "lodash"
import {withTranslation} from "react-i18next"
import {BaseInput} from "components/inputs"
import {BaseButton, SecondaryButton, AddItemCircleButton} from "components/buttons"
import {KlassappTable, KlassappTableHeader} from "uiKit"
import {KlassappTableProps} from "types/common"
import {handleError, checkPermission, toastError, toastWarning, scrollTableToBottom} from "helpers"
import {admissionService} from "services"
import {KlassappTableHOC} from "HOC"
import {BaseDepartmentId} from "types/departments"
import {PermissionsRequired} from "components/PermissionsRequired"
import {getPermissionsByDepartment} from "sections/Settings/helper"
import {Model} from "Model"
import {PermissionUserType} from "types/permission"
import {BaseNewFilter} from "uiKit"
import styles from "./LeadSources.module.css"
import {FilterKey} from "types/filter"

type PageProps = {
    t: Function
    model: Model
    departmentId: number
}

type Props = KlassappTableProps & PageProps

type State = {
    newItemData: any
    oldItemData: any
    isSubmitting: boolean
    isGettingMaxId: boolean
    search: string
    permissions: PermissionUserType
}

class LeadSources extends React.Component<Props, State> {
    constructor(props) {
        super(props)
        const storageData = this.props.model.getStorageFilter(FilterKey.LeadSources)
        this.state = {
            newItemData: null,
            oldItemData: null,
            isSubmitting: false,
            isGettingMaxId: false,
            search: !isEmpty(storageData) ? storageData.search : "",
            permissions: {staff: []}
        }
    }

    componentDidMount() {
        this.getPermissions()
        this.props.dispatchFunc([
            {key: "getPageTitle", func: this.getPageTitle},
            {key: "getListData", func: this.getData},
            {key: "getFields", func: this.getFields},
            {key: "getColumns", func: this.getColumns},
            {key: "getTableHeaderActions", func: this.getTableHeaderActions},
            {key: "getMenuActions", func: this.getMenuActions},
            {key: "onClickDeleteMulti", func: this.onClickDeleteMulti},
            {key: "onClickDuplicateMulti", func: this.onClickDuplicateMulti},
            {key: "onClickEdit", func: this.onClickEdit},
            {key: "onClickDelete", func: this.onClickDelete}
        ])
        this.getData()
    }

    getPermissions = () => {
        const permissions = getPermissionsByDepartment(BaseDepartmentId.Admissions)
        this.setState({permissions})
    }

    getPageTitle = () => {
        return this.props.t("admission.leadSources.leadSources")
    }

    getFields = () => {
        const {t} = this.props
        return [
            t("admission.leadSources.etrackCode"),
            t("admission.leadSources.title"),
            t("admission.leadSources.leadType"),
            t("admission.leadSources.description")
        ]
    }

    getTableHeaderActions = (isShowDuplicateBtn = true, checkedData = []) => {
        const canWrite = checkPermission(this.state.permissions, this.props.model)
        if (!canWrite) {
            return []
        }
        const {t, onClickShowConfirmModal} = this.props
        const actions = [
            {
                title: t("common:action.delete"),
                icon: "DELETE",
                action: () => onClickShowConfirmModal("DELETE")
            }
        ]
        if (isShowDuplicateBtn) {
            actions.push({
                title: t("common:action.duplicate"),
                icon: "DUPLICATE",
                action: () => this.onClickDuplicateMulti()
            })
        }
        return actions
    }

    canShowDeleteIcon = () => {
        return checkPermission(this.state.permissions, this.props.model)
    }

    canShowEditIcon = () => {
        return checkPermission(this.state.permissions, this.props.model)
    }

    getMenuActions = () => {
        const {t} = this.props
        return [
            {
                title: t("common:action.edit"),
                icon: "EDIT",
                action: this.onClickEdit,
                canShow: this.canShowEditIcon
            },
            {
                title: t("common:action.delete"),
                icon: "DELETE",
                action: this.onClickDelete,
                canShow: this.canShowDeleteIcon
            }
        ]
    }

    getData = async () => {
        this.props.dispatch({isLoading: true})
        const params = this.getParams()
        try {
            const {data, total} = await admissionService.getAllLeadSources(params)
            const newData = data.map((item) => {
                item.id = item.admissionsLeadSourceId
                return item
            })
            this.props.dispatch({data: newData, total})
        } catch (e) {
            handleError(e)
        } finally {
            this.props.dispatch({isLoading: false})
        }
    }

    getParams = () => {
        const {search} = this.state
        const {page, pageSize, orderField} = this.props
        const params: any = {
            filter: {search},
            range: {
                page,
                pageSize
            }
        }
        if (!isEmpty(orderField)) {
            params.sort = {
                orderBy: orderField.field,
                orderDir: orderField.order
            }
        }
        return params
    }

    getColumns = () => {
        const {t} = this.props

        return [
            {
                title: t("admission.leadSources.etrackCode"),
                field: "code",
                style: {minWidth: "200px"},
                sortable: true
            },
            {
                title: t("admission.leadSources.title"),
                field: "name",
                style: {minWidth: "200px"},
                sortable: true
            },
            {
                title: t("admission.leadSources.leadType"),
                field: "leadType",
                style: {minWidth: "200px"},
                sortable: true
            },
            {
                title: t("admission.leadSources.description"),
                field: "description",
                style: {minWidth: "200px"},
                sortable: false
            }
        ]
    }

    onClickDeleteMulti = async () => {
        const checkedItems = this.props.data.filter((item) => item.isChecked)
        const payload = checkedItems.map((item) => item.id)
        try {
            this.props.dispatch({isLoading: true, isShowTableHeaderAction: false, isHideMenuActions: false})
            await admissionService.deleteLeadSource(payload)
            await this.getData()
        } catch (e) {
            handleError(e)
        } finally {
            this.props.dispatch({isLoading: false})
        }
    }

    onClickDuplicateMulti = async () => {
        const {data} = this.props
        const payload = data
            .filter((item) => item.isChecked)
            .map(({code, name, leadType, description}) => ({
                name: `${name} (Copy)`,
                code: `${code} (Copy)`,
                leadType,
                description
            }))
        try {
            this.props.dispatch({isLoading: true, isShowTableHeaderAction: false, isHideMenuActions: false})
            await admissionService.duplicateLeadSource(payload)
            await this.getData()
        } catch (e) {
            handleError(e)
        } finally {
            this.props.dispatch({isLoading: false})
        }
    }

    onClickEdit = (editItem) => {
        const {newItemData} = this.state
        if (newItemData) {
            toastWarning(this.props.t("common:validation.saveBeforeEdit"))
            return
        }
        this.setState(
            {
                newItemData: {
                    ...editItem,
                    nameData: editItem.name,
                    codeData: editItem.code,
                    leadTypeData: editItem.leadType,
                    descriptionData: editItem.description
                },
                oldItemData: {...editItem}
            },
            () => {
                const newData = this.props.data.map((item) => {
                    if (item.id === editItem.id) {
                        item.code = this.renderCodeInput()
                        item.name = this.renderNameInput()
                        item.leadType = this.renderLeadTypeInput()
                        item.description = this.renderDescriptionInput()
                        return item
                    }
                    return item
                })
                this.props.dispatch({data: newData})
            }
        )
    }

    onClickDelete = async (deletedItem) => {
        if (deletedItem.isForm) {
            this.setState({newItemData: null, oldItemData: null})
            await this.getData()
            return
        }
        try {
            this.props.dispatch({isLoading: true})
            this.setState({newItemData: null, oldItemData: null})
            await admissionService.deleteLeadSource([deletedItem.id])
            await this.getData()
        } catch (e) {
            handleError(e)
        } finally {
            this.props.dispatch({isLoading: false})
        }
    }

    onChangeNewItemData = (key, value) => {
        const {newItemData} = this.state
        newItemData[key] = value
        const newData = this.props.data.map((item) => {
            if (item.id === newItemData.id) {
                item.name = this.renderNameInput()
                item.code = this.renderCodeInput()
                item.leadType = this.renderLeadTypeInput()
                item.description = this.renderDescriptionInput()
                return item
            }
            return item
        })
        this.setState({newItemData})
        this.props.dispatch({data: newData})
    }

    renderCodeInput = () => {
        const newItemData = this.state.newItemData || {}
        return (
            <BaseInput
                value={newItemData.codeData}
                placeholder="Code"
                onChange={(value) => this.onChangeNewItemData("codeData", value)}
            />
        )
    }

    renderNameInput = () => {
        const newItemData = this.state.newItemData || {}
        return (
            <BaseInput
                value={newItemData.nameData}
                placeholder="Title"
                onChange={(value) => this.onChangeNewItemData("nameData", value)}
            />
        )
    }

    renderLeadTypeInput = () => {
        const newItemData = this.state.newItemData || {}
        return (
            <BaseInput
                value={newItemData.leadTypeData}
                placeholder="Lead Type"
                onChange={(value) => this.onChangeNewItemData("leadTypeData", value)}
            />
        )
    }

    renderDescriptionInput = () => {
        const newItemData = this.state.newItemData || {}
        return (
            <BaseInput
                value={newItemData.descriptionData}
                placeholder="Description"
                onChange={(value) => this.onChangeNewItemData("descriptionData", value)}
            />
        )
    }

    getMaxId = async () => {
        try {
            this.setState({isGettingMaxId: true})
            const maxId = await admissionService.getMaxIdLeadSources()
            return Number.parseInt(maxId, 10)
        } catch (e) {
            handleError(e)
            return 0
        } finally {
            this.setState({isGettingMaxId: false})
        }
    }

    onClickAddItem = async () => {
        const {isGettingMaxId} = this.state
        const {isLoading} = this.props
        if (isLoading || isGettingMaxId) return
        const maxId: number = await this.getMaxId()
        const newItemData = {
            id: uuid(),
            nameData: "",
            codeData: `LS${maxId + 1}`,
            leadTypeData: "",
            descriptionData: "",
            isForm: true
        }
        this.setState(
            {
                newItemData,
                oldItemData: null
            },
            () => {
                const {data} = this.props
                const newItem = {
                    ...newItemData,
                    name: this.renderNameInput(),
                    code: this.renderCodeInput(),
                    leadType: this.renderLeadTypeInput(),
                    description: this.renderDescriptionInput()
                }
                data.push(newItem)
                this.props.dispatch({data})
                scrollTableToBottom(this.props.tableRef)
            }
        )
    }

    validateDataBeforeSubmit = () => {
        const {nameData, codeData} = this.state.newItemData
        if (!nameData) {
            toastError(this.props.t("common:validation.cantEmpty", {field: "The Title"}))
            return false
        }
        if (!codeData) {
            toastError(this.props.t("common:validation.cantEmpty", {field: "The ID"}))
            return false
        }
        return true
    }

    onClickSave = async () => {
        if (!this.validateDataBeforeSubmit()) {
            return
        }
        const {nameData, codeData, leadTypeData, descriptionData, isForm, id} = this.state.newItemData
        if (isForm) {
            const submitData = {
                name: nameData,
                code: codeData,
                leadType: leadTypeData,
                description: descriptionData
            }
            await this.createLeadSource(submitData)
        } else {
            const submitData = {name: nameData, code: codeData, leadType: leadTypeData, description: descriptionData}
            await this.updateLeadSource(id, submitData)
        }
        this.setState({newItemData: null, oldItemData: null}, () => {
            this.getData()
        })
    }

    createLeadSource = async (data) => {
        try {
            this.props.dispatch({isLoading: true})
            this.setState({isSubmitting: true})
            await admissionService.createLeadSource(data)
        } catch (e) {
            handleError(e)
        } finally {
            this.props.dispatch({isLoading: false})
            this.setState({isSubmitting: false})
        }
    }

    updateLeadSource = async (id, data) => {
        try {
            this.props.dispatch({isLoading: true})
            this.setState({isSubmitting: true})
            await admissionService.updateLeadSource(id, data)
        } catch (e) {
            handleError(e)
        } finally {
            this.props.dispatch({isLoading: false})
            this.setState({isSubmitting: false})
        }
    }

    onClickCancel = () => {
        const {oldItemData, isSubmitting} = this.state
        const {data, isLoading} = this.props
        if (isLoading || isSubmitting) {
            return
        }
        const newData = data
            .filter((item) => !item.isForm)
            .map((item) => {
                if (oldItemData && oldItemData.id === item.id) {
                    item.name = oldItemData.name
                    item.code = oldItemData.code
                    item.leadType = oldItemData.leadType
                    item.description = oldItemData.description
                    return item
                }
                return item
            })
        this.setState({newItemData: null, oldItemData: null})
        this.props.dispatch({data: newData})
    }

    onSearchInput = (search) => {
        this.props.model.updateStorageFilter(FilterKey.LeadSources, {search, page: 1})
        this.props.dispatch({page: 1})
        this.setState({search}, () => {
            this.getData()
        })
    }

    render() {
        const {newItemData, isSubmitting, isGettingMaxId, permissions, search} = this.state
        const {
            t,
            page,
            total,
            pageSize,
            columns,
            data,
            menuActions,
            allFields,
            fields,
            tableHeaderActions,
            isHideMenuActions,
            isShowTableHeaderAction,
            orderField,
            isLoading
        } = this.props

        return (
            <div className={styles.wrapper}>
                <BaseNewFilter searchValue={search} onSearchInput={this.onSearchInput} />
                <KlassappTableHeader
                    isShowAction={isShowTableHeaderAction}
                    actions={tableHeaderActions}
                    page={page}
                    total={total}
                    defaultPageSize={pageSize}
                    onChangePage={this.props.onChangePage}
                    onChangeRowPerPage={this.props.onChangeRowPerPage}
                    fields={fields}
                    allFields={allFields}
                    onChangeFields={this.props.onChangeFields}
                    onChangeAllFields={this.props.onChangeAllFields}
                    onDraggableColumn={this.props.onDraggableColumn}
                />
                <KlassappTable
                    columns={columns}
                    data={data}
                    menuActions={isHideMenuActions ? [] : menuActions}
                    isLoading={isLoading}
                    fields={fields}
                    allFields={allFields}
                    orderField={orderField}
                    isShowCheckedColumn
                    tableRef={this.props.tableRef}
                    onClickRowItem={() => {}}
                    onChangeFields={this.props.onChangeFields}
                    onUpdateRowData={this.props.onUpdateRowData}
                    onUpdateTableData={this.props.onUpdateTableData}
                    onClickSortColumn={this.props.onClickSortColumn}
                    onDraggableColumn={this.props.onDraggableColumn}
                    onChangeAllFields={this.props.onChangeAllFields}
                />
                <PermissionsRequired permissions={permissions}>
                    {!newItemData ? (
                        <div className={styles.buttonWrap}>
                            {!isGettingMaxId && !isLoading && <AddItemCircleButton onClick={this.onClickAddItem} />}
                        </div>
                    ) : (
                        <div className={styles.buttonWrap}>
                            <SecondaryButton
                                title={t("common:action.cancel")}
                                onClick={this.onClickCancel}
                                className={styles.cancelBtn}
                            />
                            <BaseButton
                                title={t("common:action.save").toUpperCase()}
                                onClick={this.onClickSave}
                                loading={isSubmitting}
                            />
                        </div>
                    )}
                </PermissionsRequired>
            </div>
        )
    }
}

export default KlassappTableHOC(withTranslation(["admission", "common"])(LeadSources))
